import { DocumentVersionStatus, type SignedCommand } from '@qualicloud/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { SignatureDialog } from '@/components/signature/signature-dialog';
import { DocumentStatusBadge } from '@/components/validity-semaphore';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconCheckCircle,
  IconClipboardCheck,
  IconEye,
  LoadingRegion,
  Skeleton,
  useToast,
} from '@/design-system';
import { documentsApi, queryKeys, signedActInvalidations } from '@/lib/api';
import type { MasterListRow } from '@/lib/api/types';
import { formatDateOnly } from '@/lib/format/datetime';
import { PageBody, PageHeader } from '@/shell/app-shell';

type PendingDecision = { row: MasterListRow; decision: 'APPROVED' | 'REJECTED' };

/**
 * Fila de aprovação.
 *
 * Usa o mesmo endpoint da Lista Mestra com `onlyPendingMyApproval`, o que
 * garante que a fila e a lista nunca discordem: é a MESMA consulta, a MESMA
 * resolução de ACL e a mesma regra de elegibilidade.
 *
 * Toda decisão passa pelo `<SignatureDialog>` — não existe caminho para aprovar
 * sem assinatura. Isso é o oposto do legado, em que aprovar era um `UPDATE` de
 * coluna, sem reautenticação, sem significado e sem verificação de autoridade
 * ou de tenant (BUG-44): qualquer usuário autenticado aprovava qualquer
 * documento de qualquer empresa sabendo o identificador.
 */
export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pending, setPending] = useState<PendingDecision | null>(null);

  const queueQuery = useQuery({
    queryKey: queryKeys.documents.pendingApprovals(),
    queryFn: () =>
      documentsApi.list({
        onlyPendingMyApproval: true,
        page: 1,
        pageSize: 100,
        sortBy: 'revisionDate',
        sortDir: 'asc',
      }),
  });

  const decide = useMutation({
    mutationFn: (params: {
      row: MasterListRow;
      command: SignedCommand;
      decision: 'APPROVED' | 'REJECTED';
    }) =>
      documentsApi.decide(params.row.documentId, params.row.versionId, {
        ...params.command,
        decision: params.decision,
      }),
  });

  const items = queueQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Minhas aprovações"
        description="Versões de documento em que você é aprovador designado e ainda não registrou decisão. Aprovar ou reprovar exige assinatura eletrônica."
        actions={
          items.length > 0 ? (
            <Badge tone="warning" srPrefix="Total de pendências:">
              {items.length} {items.length === 1 ? 'pendência' : 'pendências'}
            </Badge>
          ) : undefined
        }
      />

      <PageBody>
        <LoadingRegion loading={queueQuery.isPending} label="Carregando a fila de aprovação…">
          {queueQuery.isPending ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-28 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <Card>
              <EmptyState
                icon={<IconCheckCircle size={22} />}
                title="Nenhuma aprovação pendente"
                description="Você está em dia. Quando uma versão for submetida e você for aprovador designado dela, ela aparecerá aqui e você receberá uma notificação."
                action={
                  <Button asChild variant="secondary">
                    <Link to="/documentos">Ir para a Lista Mestra</Link>
                  </Button>
                }
              />
            </Card>
          ) : (
            <ul className="flex flex-col gap-3">
              {items.map((row) => (
                <li key={row.versionId}>
                  <Card>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-content">
                          <Link
                            to="/documentos/$documentId"
                            params={{ documentId: row.documentId }}
                            className="hover:text-brand hover:underline"
                          >
                            {row.code ? (
                              <span className="font-mono text-sm">{row.code} · </span>
                            ) : null}
                            {row.name}
                          </Link>
                        </h2>

                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <DocumentStatusBadge status={row.status} />
                          <Badge tone="outline" srPrefix="Versão:">
                            v{row.version}
                          </Badge>
                          <Badge tone="outline" srPrefix="Tipo:">
                            {row.documentType}
                          </Badge>
                        </div>

                        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm sm:grid-cols-[auto_1fr_auto_1fr]">
                          <dt className="text-content-subtle">Data da versão</dt>
                          <dd className="text-content qc-tabular">
                            {formatDateOnly(row.revisionDate)}
                          </dd>
                          <dt className="text-content-subtle">Válido até</dt>
                          <dd className="text-content qc-tabular">
                            {row.validUntil ? formatDateOnly(row.validUntil) : 'Sem validade'}
                          </dd>
                        </dl>
                      </div>

                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <Button asChild variant="ghost" leadingIcon={<IconEye size={15} />}>
                          <Link to="/documentos/$documentId" params={{ documentId: row.documentId }}>
                            Analisar
                          </Link>
                        </Button>
                        <Button
                          variant="primary"
                          leadingIcon={<IconClipboardCheck size={15} />}
                          onClick={() => setPending({ row, decision: 'APPROVED' })}
                        >
                          Aprovar
                        </Button>
                        <Button
                          variant="danger-outline"
                          onClick={() => setPending({ row, decision: 'REJECTED' })}
                        >
                          Reprovar
                        </Button>
                      </div>
                    </div>

                    <p className="mt-3 rounded-md border border-info bg-info-subtle px-3 py-2 text-xs text-info-content">
                      Antes de decidir, abra o documento e leia a versão submetida. A sua assinatura
                      declara que você analisou o conteúdo.
                    </p>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </LoadingRegion>
      </PageBody>

      {pending ? (
        <SignatureDialog
          open
          onOpenChange={(open) => {
            if (!open) setPending(null);
          }}
          act={pending.decision === 'APPROVED' ? 'DOCUMENT_APPROVE' : 'DOCUMENT_REJECT'}
          recordId={pending.row.versionId}
          recordLabel={
            pending.row.code ? `${pending.row.code} — ${pending.row.name}` : pending.row.name
          }
          recordVersion={pending.row.version}
          recordDetails={[
            { label: 'Tipo', value: pending.row.documentType },
            { label: 'Data da versão', value: formatDateOnly(pending.row.revisionDate) },
            {
              label: 'Situação atual',
              value:
                pending.row.status === DocumentVersionStatus.IN_REVIEW
                  ? 'Pendente de aprovação'
                  : pending.row.statusLabel,
            },
          ]}
          onSign={async (command) => {
            await decide.mutateAsync({
              row: pending.row,
              command,
              decision: pending.decision,
            });
          }}
          onSigned={() => {
            const row = pending.row;
            setPending(null);
            void Promise.all(
              signedActInvalidations(row.documentId).map((key) =>
                queryClient.invalidateQueries({ queryKey: key }),
              ),
            );
            toast({
              tone: 'success',
              title:
                pending.decision === 'APPROVED' ? 'Versão aprovada' : 'Versão reprovada',
              description:
                'A decisão e a sua assinatura eletrônica foram gravadas na mesma transação. O manifesto do documento já reflete o ato.',
            });
          }}
        />
      ) : null}
    </>
  );
}
